'use client';

import './payment-method-card.scss';

import { format, isValid, parse } from 'date-fns';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Badge, BadgeProps } from '@sola/ark-core/atoms/badge/badge.js';
import {
  ActionCard,
  ActionCardBody,
  ActionCardDivider,
  ActionCardHeader,
  ActionCardSection,
} from '@sola/ark-core/molecules/action-card/action-card.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@sola/ark-core/molecules/tooltip/tooltip.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { CommitmentTypeEnum } from '@sola/schema/dotcom/cart/commitments.js';
import { CreditCardEnum } from '@sola/schema/dotcom/checkout/billingInfo.js';
import { CommitmentStatusEnum } from '@sola/schema/dotcom/supporters/commitments.js';
import {
  GraphQLCardExpiryStatus,
  GraphQLCommitment,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.js';
import { IconNames } from '@sola/schema/lib/icons/icon-names.js';
import { slugify } from '@sola/utils/string.js';

import { CommitmentAvatar } from '../../atoms/commitment-avatar/commitment-avatar';
import { PaymentMethodCardFooter } from './payment-method-card-footer';

/**
 * Get icon component
 * @TODO will change this after ark-icon refactor.
 */
const getIconName = (paymentMethod: GraphQLSupporterPaymentMethod) => {
  if (paymentMethod.creditCard?.cardType) {
    return (
      {
        [CreditCardEnum.Amex]: 'card-amex',
        'american express': 'card-amex',
        [CreditCardEnum.Discover]: 'card-discover',
        [CreditCardEnum.Mastercard]: 'caad-mastercard',
        'master card': 'card-mastercard',
        [CreditCardEnum.Visa]: 'card-visa',
      }[paymentMethod.creditCard.cardType.toLowerCase()] || 'credit-card-02' // what in tarnation?!
    );
  }

  if (paymentMethod.ach) {
    return 'bank';
  }

  // What else?
  return 'card';
};

/**
 * Get payment method account nickname.
 * This assumes we always want to display the nickname.
 */
const getNickname = (
  paymentMethod: GraphQLSupporterPaymentMethod,
  t: TFunction,
  matchingMethods?: GraphQLSupporterPaymentMethod[],
) => {
  if (paymentMethod.accountNickname) {
    return paymentMethod.accountNickname;
  }
  const index =
    matchingMethods && matchingMethods?.length > 1
      ? `${matchingMethods.indexOf(paymentMethod) + 1}`
      : '';
  // TODO using paymentMethod.creditCard instead of checking payment type here and elsewhere because our graphql paymentType is not an enum.
  if (paymentMethod.creditCard) {
    return t('payments.methodNickname.card', {
      cardType: paymentMethod.creditCard.cardType,
      index,
    });
  }
  if (paymentMethod.ach) {
    return t('payments.methodNickname.ach', {
      index,
    });
  }
};

/**
 * Get payment method label.
 */
const getLabel = (
  paymentMethod: GraphQLSupporterPaymentMethod,
  t: TFunction,
) => {
  const { creditCard, ach } = paymentMethod;
  if (creditCard) {
    return t('payments.methodLabel.card', {
      cardType: creditCard.cardType,
      lastFour: creditCard.lastFour,
    });
  }
  if (paymentMethod.ach) {
    return t('payments.methodLabel.ach', {
      lastFour: ach?.lastFourOfAccountNumber,
    });
  }
};

/**
 * Get payment method details.
 */
const getDetails = (
  paymentMethod: GraphQLSupporterPaymentMethod,
  t: TFunction,
) => {
  if (paymentMethod.creditCard) {
    const expiryDate = paymentMethod.creditCard.expirationDate
      ? parse(paymentMethod.creditCard.expirationDate, 'MM/yyyy', new Date())
      : null;
    if (!isValid(expiryDate)) {
      return;
    }
    if (paymentMethod.creditCard.expiryStatus === 'EXPIRED') {
      return t('payments.methodDetails.expiredCard', {
        expirationDate: format(expiryDate!, 'M/yy'),
        interpolation: { escapeValue: false },
      });
    }
    return t('payments.methodDetails.card', {
      expirationDate: format(expiryDate!, 'M/yy'),
      interpolation: { escapeValue: false },
    });
  }
  if (paymentMethod.ach) {
    return t('payments.methodDetails.ach', {
      lastFour: paymentMethod.ach.lastFourOfAccountNumber,
    });
  }
};

export type PaymentMethodCardProps = {
  commitments?: GraphQLCommitment[];
  /**
   * Used to provide context aware logic such as generated nickname
   */
  matchingMethods?: GraphQLSupporterPaymentMethod[];
  paymentMethod: GraphQLSupporterPaymentMethod;
};

/**
 * Get payment card data.
 */
export const usePaymentMethodCardData = ({
  paymentMethod,
  commitments,
  matchingMethods,
}: PaymentMethodCardProps) => {
  const { t } = useTranslation('my');
  return {
    t,
    nickname: getNickname(paymentMethod, t, matchingMethods),
    iconName: getIconName(paymentMethod),
    label: getLabel(paymentMethod, t),
    details: getDetails(paymentMethod, t),
    recurringCommitments: commitments?.length
      ? commitments
          .filter(
            (commitment) =>
              commitment.isRecurring &&
              commitment.status === CommitmentStatusEnum.Active,
          )
          .sort((a, b) => {
            const sponsorship = CommitmentTypeEnum.ChildSponsorship;
            if (
              a.commitmentType === sponsorship &&
              b.commitmentType !== sponsorship
            ) {
              return -1;
            }
            if (
              a.commitmentType !== sponsorship &&
              b.commitmentType === sponsorship
            ) {
              return 1;
            }
            return 0;
          })
      : [],
    // TODO will prob need to handle other cases.
    isWarning:
      paymentMethod.creditCard?.expiryStatus ===
      GraphQLCardExpiryStatus.ExpiringSoon,
    isError:
      paymentMethod.creditCard?.expiryStatus ===
      GraphQLCardExpiryStatus.Expired,
  };
};

export const PaymentMethodCard: React.FC<PaymentMethodCardProps> = (
  props: PaymentMethodCardProps,
) => {
  const {
    details,
    isError,
    isWarning,
    label,
    nickname,
    recurringCommitments,
    iconName,
    t,
  } = usePaymentMethodCardData(props);
  const { paymentMethod } = props;
  const hasBadge = paymentMethod.creditCard?.expiryStatus;

  return (
    <ActionCard
      className={cn(`PaymentMethodCard `, {
        'PaymentMethodCard--warning': isWarning,
        'PaymentMethodCard--error': isError,
        [`PaymentMethodCard--${slugify(paymentMethod.paymentType!)}`]:
          !!paymentMethod.paymentType,
      })}
      // Easy code > debatable rule...
      // eslint-disable-next-line no-nested-ternary
      color={isWarning ? 'warning' : isError ? 'negative' : undefined}
    >
      <ActionCardSection>
        <ActionCardHeader
          className={cn([
            // TODO: this is a temporary solution to make room for badge, but we need a scalable solution because it will keep reocurring.
            hasBadge
              ? 'mb-comfy-xs md:mb-comfy-sm'
              : 'mb-comfy-sm md:mb-comfy-md',
          ])}
        >
          {/* TODO our label utilities have some issues */}
          <h3 className="label-potent md:label-potent label-md md:label-lg">
            {nickname}
          </h3>

          {paymentMethod.creditCard?.expiryStatus && (
            <Badge
              {...({
                [GraphQLCardExpiryStatus.ExpiringSoon]: {
                  color: 'warning',
                  iconBefore: 'alert-triangle',
                  label: t('cardExpiring'),
                },
                [GraphQLCardExpiryStatus.Expired]: {
                  color: 'negative',
                  iconBefore: 'alert-circle',
                  label: t('cardExpired'),
                },
              }[paymentMethod.creditCard.expiryStatus] as BadgeProps)}
            />
          )}
        </ActionCardHeader>
        <ActionCardBody>
          <ArkIcon
            className={cn('PaymentMethodCard__icon', [
              paymentMethod?.ach &&
                'PaymentMethodCard__icon-bank rounded-sm p-snug-xl',
            ])}
            iconName={iconName as IconNames}
          />
          <div className="">
            <span className="block label-md label-moderate md:label-lg mb-snug-md">
              {label}
            </span>
            {details && (
              <span
                className={cn(
                  'block label-sm label-moderate label-neutral-mild md:label-md',
                  {
                    'label-warning-moderate': isWarning,
                    'label-negative-moderate': isError,
                  },
                )}
              >
                {details}
              </span>
            )}
          </div>
          {!!recurringCommitments?.length && (
            <div className="flex items-center gap-snug-md">
              {recurringCommitments.slice(0, 3).map((commitment, i) => {
                const hasMore = i === 2 && recurringCommitments.length > 3;
                const remainingCount = recurringCommitments.length - 2;

                return (
                  <div key={commitment.id} className="-ml-comfy-md">
                    <CommitmentAvatar
                      isStacked
                      {...(!hasMore
                        ? { commitment }
                        : { text: `+${remainingCount}` })}
                    />
                  </div>
                );
              })}
              {!!recurringCommitments.length && (
                <Tooltip>
                  <TooltipTrigger>
                    <button
                      type="button"
                      className="PaymentMethodCard__tticon flex items-center justify-center"
                      aria-label="Additional information"
                      onClick={(e) => {
                        e.preventDefault();
                      }}
                    >
                      <ArkIcon iconName="info-circle" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    className="label-md lg:label-lg"
                    // side="right"
                    alignOffset={-12}
                    onPointerDownOutside={(e) => {
                      e.preventDefault();
                    }}
                  >
                    {t('payments.methodCommitmentsTooltip', {
                      count: recurringCommitments.length,
                    })}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          )}
        </ActionCardBody>
      </ActionCardSection>
      <ActionCardDivider />
      <PaymentMethodCardFooter
        recurringCommitments={recurringCommitments}
        paymentMethod={paymentMethod}
      />
    </ActionCard>
  );

  // return (
  //   <SupporterActionCard
  //     purpose={status === 'active' ? 'neutral' : getPurposeByStatus[status]}
  //     headerLeftElement={
  //       <h3 className="label-potent label-md label-neutral-amplified md:label-lg">
  //         {TEMP_NICKNAME}
  //       </h3>
  //     }
  //     headerRightElement={
  //       status !== 'active' && (
  //         <StatusPill
  //           purpose={getPurposeByStatus[status]}
  //           label={getLabelByStatus[status]}
  //         />
  //       )
  //     }
  //     bodyLeftElement={
  //       <PaymentMethodTypeAndInfo
  //         paymentMethod={paymentMethod}
  //         status={status}
  //       />
  //     }
  //     bodyRightElement={
  //       <div className="flex items-center gap-snug-lg">
  //         {Array.isArray(commitments) && commitments.length > 0 && (
  //           <>
  //             <StackedCommitmentAvatars
  //               purpose="neutral"
  //               commitments={commitments}
  //             />
  //             <PaymentMethodCardTooltip />
  //           </>
  //         )}
  //       </div>
  //     }
  //     footerRightElement={
  //       <PaymentMethodCardFooter
  //         isPrimary={isPrimary}
  //         editAction={editAction}
  //         removeAction={removeAction}
  //       />
  //     }
  //   />
  // );
};
